import { EventIdentifier, FeatureTeamEvent, TrackingEventType, useAnalytics, useIntl } from '@capsicum/common';
import { TextInput } from '@capsicum/common-web';
import {
	type UsernameRegisterStepProps,
	usernameRegistrationStepAtom,
	useUsernameStepValidation
} from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { AgeConfirmation } from '../AgeConfirmation';
import { MarketingConcent } from '../MarketingConcent';
import { Checkboxes } from '../Register.styled';
import { StepperPage } from '../StepperPage';
import { TermsAndConditions } from '../TermsAndConditions';

export const UsernameStep = ({
	isCountryStepVisible,
	isEmailStepVisible,
	onValidityChange,
	setUsername
}: UsernameRegisterStepProps) => {
	const { $t } = useIntl();
	const analytics = useAnalytics();

	const { suggestions } = useAtomValue(usernameRegistrationStepAtom);

	useUsernameStepValidation({ isCountryStepVisible, isEmailStepVisible, onValidityChange });

	const suggestedNames = useMemo(() => {
		if (!suggestions.length) return <S.TextSuggestions />;

		analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
			feature_team: FeatureTeamEvent.ORFT,
			page: 'username_entry_page',
			text: 'show_username_suggestions'
		});

		return (
			<S.TextSuggestions>
				{$t('auth.usernameSuggestions')}
				{suggestions
					.flatMap(name => [
						<S.TextSuggestionsHighlighted key={name} onClick={() => setUsername(name)}>
							{name}
						</S.TextSuggestionsHighlighted>,
						', '
					])
					.slice(0, -1)}
			</S.TextSuggestions>
		);
	}, [$t, setUsername, suggestions, analytics]);

	const checkboxes = useMemo(() => {
		const checkboxes: Array<JSX.Element> = [];

		if (!isEmailStepVisible)
			checkboxes.push(<AgeConfirmation key="age-confirmations" />, <MarketingConcent key="marketing-concent" />);

		if (!isCountryStepVisible) checkboxes.push(<TermsAndConditions key="terms-and-conditions" />);

		return checkboxes;
	}, [isEmailStepVisible, isCountryStepVisible]);

	useEffect(
		() =>
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
				feature_team: FeatureTeamEvent.ORFT,
				page: 'username_entry_page'
			}),
		[analytics]
	);

	return (
		<StepperPage
			subtitle={$t('auth.registration.usernameStep.subtitle')}
			title={$t('auth.registration.usernameStep.title')}
		>
			<div>
				<TextInput
					autoCapitalize="none"
					label={$t('auth.registrationUsername')}
					name="username"
					supportingText={$t('auth.usernameHelper')}
					testID="registration-form-username-input"
				/>
				{suggestedNames}
			</div>

			{checkboxes.length > 0 && <Checkboxes>{checkboxes}</Checkboxes>}
		</StepperPage>
	);
};

namespace S {
	export const TextSuggestions = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		height: ${({ theme }) => theme.spacings.xsmall};
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const TextSuggestionsHighlighted = styled.span`
		${({ theme }) => theme.fonts.text.caption2};
		cursor: pointer;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
}
